import { useEffect, useRef } from 'react';

interface ScriptEmbedProps {
  attribs?: Record<string, string>;
  inline?: string;
}

/**
 * Renders a <script> coming from editorial content (embeds/infographics in
 * core/html blocks).
 *
 * Renders NOTHING server-side: React doesn't execute a <script> injected at
 * hydration, and a malformed tag crashes renderToString ("Invalid tag").
 * We therefore inject a real <script> into the DOM after hydration so the
 * embed only runs client-side.
 */
export function ScriptEmbed({ attribs, inline }: ScriptEmbedProps) {
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const script = document.createElement('script');

    // Copy over the attributes (src, async, defer, type, charset, data-*, etc.)
    for (const [name, value] of Object.entries(attribs ?? {})) {
      try {
        script.setAttribute(name, value);
      } catch {
        // Invalid attribute name: skip it instead of breaking everything.
      }
    }

    if (inline) {
      script.textContent = inline;
    }

    container.appendChild(script);

    return () => {
      script.remove();
    };
  }, [attribs, inline]);

  return <span ref={containerRef} data-script-embed="true" />;
}

export default ScriptEmbed;
